import ApiManager from "./ApiManager";
import { CryptoJSAesEncrypt } from "./cryp.js";
import config from "../config.js";

const partnerUrl = config().PARTNER_URL;

export default class PartnerApi extends ApiManager {
  static getUtcDateTime() {
    return new Date().toISOString().split(".")[0];
  }

  static buildStartSessionSignature(licenseNo, databaseName, datetime) {
    return CryptoJSAesEncrypt(`${licenseNo}${databaseName}${datetime}`);
  }

  static extractApiError(data, fallbackStatus) {
    const code = Number(data?.code ?? data?.error?.code ?? fallbackStatus ?? 0);
    const rawMessage =
      data?.message ||
      data?.error?.message ||
      data?.more ||
      "İşlem sırasında bir hata oluştu.";

    if (code === 1013) {
      return {
        code,
        message: "Oturumun süresi doldu. Lütfen işlemi tekrar başlatın.",
      };
    }

    if (code === 1012) {
      if (String(rawMessage).toLowerCase().includes("invalid token")) {
        return {
          code,
          message:
            "Geçersiz veya süresi dolmuş oturum. Lütfen işlemi AKEAD üzerinden yeniden başlatın.",
        };
      }

      return {
        code,
        message: "Bu sayfa AKEAD'dan açılmalıdır.",
      };
    }

    if (
      code === 502 &&
      String(rawMessage).toLowerCase().includes("temporarily unavailable")
    ) {
      return {
        code,
        message:
          "Ödeme servisi şu anda geçici olarak kullanılamıyor. Lütfen daha sonra tekrar deneyin.",
      };
    }

    if (code === 502 && String(rawMessage).toLowerCase().includes("<html")) {
      return {
        code,
        message: "Sunucu geçici olarak kullanılamıyor. Lütfen daha sonra tekrar deneyin.",
      };
    }

    return {
      code,
      message: rawMessage,
    };
  }

  static async parseJsonSafe(response) {
    const rawText = await response.text();
    if (!rawText) return null;

    try {
      return JSON.parse(rawText);
    } catch (e) {
      return {
        code: response.status,
        message: rawText,
      };
    }
  }

  static async startSession({ licenseNo, databaseName, customerInfo }) {
    const datetime = this.getUtcDateTime();

    if (!licenseNo) {
      throw new Error("startSession için licenseNo zorunludur.");
    }

    if (!databaseName) {
      throw new Error("startSession için databaseName zorunludur.");
    }

    if (!customerInfo) {
      throw new Error("startSession için customerInfo zorunludur.");
    }

    const signature = this.buildStartSessionSignature(
      licenseNo,
      databaseName,
      datetime
    );

    const response = await fetch(`${partnerUrl}/shop/startSession`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        signature,
      },
      body: JSON.stringify({
        licenseNo,
        datetime,
        databaseName,
        customerInfo,
      }),
    });

    const data = await this.parseJsonSafe(response);

    if (!response.ok || data?.code || data?.error?.code) {
      throw this.extractApiError(data, response.status);
    }

    return data;
  }

  static async getItemList({ language = "tr", token }) {
    if (!token) {
      throw new Error("itemList için token zorunludur.");
    }

    const url = new URL(`${partnerUrl}/shop/itemList`);
    url.searchParams.append("language", language);

    const response = await fetch(url.toString(), {
      method: "GET",
      headers: {
        Accept: "application/json",
        token: String(token).trim(),
      },
    });

    const data = await this.parseJsonSafe(response);

    if (!response.ok || data?.code || data?.error?.code) {
      throw this.extractApiError(data, response.status);
    }

    return data;
  }

  static async calculateCart({
    licenseNo,
    language = "tr",
    token,
    itemList = [],
  }) {
    const datetime = this.getUtcDateTime();

    if (!licenseNo) {
      throw new Error("calculateCart için licenseNo zorunludur.");
    }

    if (!token) {
      throw new Error("calculateCart için token zorunludur.");
    }

    const response = await fetch(`${partnerUrl}/shop/calculateCart`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        token: String(token).trim(),
      },
      body: JSON.stringify({
        licenseNo,
        datetime,
        language,
        itemList: itemList.map((item) => ({
          code: item.code,
          quantity: Number(item.quantity || 0),
        })),
      }),
    });

    const data = await this.parseJsonSafe(response);

    if (!response.ok || data?.code || data?.error?.code) {
      throw this.extractApiError(data, response.status);
    }

    return data;
  }

  static async confirmCart({
    licenseNo,
    language = "tr",
    token,
    itemList = [],
  }) {
    const datetime = this.getUtcDateTime();

    if (!licenseNo) {
      throw new Error("confirmCart için licenseNo zorunludur.");
    }

    if (!token) {
      throw new Error("confirmCart için token zorunludur.");
    }

    const response = await fetch(`${partnerUrl}/shop/confirmCart`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        token: String(token).trim(),
      },
      body: JSON.stringify({
        licenseNo,
        datetime,
        language,
        itemList: itemList.map((item) => ({
          code: item.code,
          quantity: Number(item.quantity || 0),
        })),
      }),
    });

    const data = await this.parseJsonSafe(response);

    if (!response.ok || data?.code || data?.error?.code) {
      throw this.extractApiError(data, response.status);
    }

    return data;
  }
}